import React from 'react';
import type { CanvasSectionBlock } from '../sectionRegistry';
import {
  coerceLexicalStateString,
  lexicalStateToPlainText,
  lexicalStateToSanitizedHtml,
  stripLexicalInlineFontFamily,
} from '../../../lib/lexicalRichText';
import {
  getTextSurfaceClasses,
  getTextSurfaceToneClass,
  resolveTextSurfaceVariant,
} from '../../../lib/elementStyleTokens';
import {
  buttonVisualStateToReactStyle,
  normalizeButtonDesignStyle,
} from '../../../lib/buttonDesignStyles';
import {
  getTypographyPreset,
  resolveTypographyPresetKey,
  resolveTypographyStyle,
  type DesignSystemTypography,
} from '../../../lib/typography';
import { TOTAL_COLUMNS } from '../layoutEngine/blockFrame';
import {
  resolveCanvasHeadingFitStyle,
  resolveHeroSlotTypographyStyle,
  type HeroSlotRole,
} from '../textBlockTypography';
import {
  RichTextEditor,
  type RichTextFontOption,
} from '../../admin/pageLayout/fields';

const DIAGNOSTIC_FORCE_TEXT_FRAME_TRUTH = false;

type WrapperProps = React.HTMLAttributes<HTMLDivElement> & {
  style?: React.CSSProperties;
};

type TextBlockRendererProps = {
  block: CanvasSectionBlock;
  blockWrapperProps: WrapperProps;
  blockStyle: React.CSSProperties;
  blockStyleMeta?: Record<string, unknown>;
  blockTransform: { widthScale: number };
  selectableBlockHitProps: Record<string, string>;
  selectionTarget: string;
  renderSelectionOverlay: (block: CanvasSectionBlock) => React.ReactNode;
  setTextMeasureNode: (blockId: string, node: HTMLElement | null) => void;
  typography: DesignSystemTypography;
  fontLibrary: Record<string, string>;
  useFluidSectionTypography: boolean;
  layerSize: { width: number; height: number };
  w: number;
  projectedWidth: string;
  isAdmin: boolean;
  isInteractPreview: boolean;
  editingTextBlockId: string | null;
  blockSelected: boolean;
  isHeroSlotBlock: boolean;
  heroSlotRole: string;
  isSubscribeSlotBlock: boolean;
  subscribeSlotRole: string;
  isEnquirySlotBlock: boolean;
  enquirySlotRole: string;
  usesFixedFrameText: boolean;
  adminPreview?: {
    viewport?: 'desktop' | 'tablet' | 'mobile';
    onStartTextEdit?: (blockId: string) => void;
    onUpdateTextContent?: (blockId: string, lexicalState: string) => void;
    onEndTextEdit?: (blockId: string) => void;
  };
  richTextFontOptions: RichTextFontOption[];
};

export function TextBlockRenderer({
  block,
  blockWrapperProps,
  blockStyle,
  blockStyleMeta,
  blockTransform,
  selectableBlockHitProps,
  selectionTarget,
  renderSelectionOverlay,
  setTextMeasureNode,
  typography,
  fontLibrary,
  useFluidSectionTypography,
  layerSize,
  w,
  projectedWidth,
  isAdmin,
  isInteractPreview,
  editingTextBlockId,
  blockSelected,
  isHeroSlotBlock,
  heroSlotRole,
  isSubscribeSlotBlock,
  subscribeSlotRole,
  isEnquirySlotBlock,
  enquirySlotRole,
  usesFixedFrameText,
  adminPreview,
  richTextFontOptions,
}: TextBlockRendererProps) {          const lexicalMode =
            String(block.content?.format ?? '').toLowerCase() === 'lexical';
          const lexicalState = lexicalMode
            ? stripLexicalInlineFontFamily(
                coerceLexicalStateString(block.content?.lexicalState, String(block.content?.text ?? ''))
              )
            : null;
          const lexicalHtml = lexicalState ? lexicalStateToSanitizedHtml(lexicalState) : '';
          const text = lexicalState
            ? lexicalStateToPlainText(lexicalState) || String((block.content?.text as string) ?? 'Text block')
            : String((block.content?.text as string) ?? 'Text block');
          const variant = String((block.content?.variant as string) ?? '').toLowerCase();
          const rawTextRole =
            blockStyleMeta?.textRole === 'heading' ||
            blockStyleMeta?.textRole === 'wordmark' ||
            blockStyleMeta?.textRole === 'body'
              ? blockStyleMeta.textRole
              : undefined;
          const isHeading =
            rawTextRole === 'heading' ||
            rawTextRole === 'wordmark' ||
            heroSlotRole === 'heading' ||
            variant === 'heading' ||
            /heading|title|wordmark/i.test(block.id);
          const isCaption = heroSlotRole === 'micro-line' || variant === 'caption';
          const isEyebrow = variant === 'eyebrow';
          const isQuote = variant === 'quote';
          const genericPresetKey = resolveTypographyPresetKey(
            block.content?.presetKey,
            isHeading ? 'sectionHeading' : isCaption || isEyebrow ? 'caption' : 'body'
          );
          const heroToneClass =
            heroSlotRole === 'heading'
              ? 'text-white drop-shadow-[0_4px_18px_rgba(0,0,0,0.42)]'
              : heroSlotRole === 'subheading'
                ? 'text-white/95 drop-shadow-[0_2px_12px_rgba(0,0,0,0.38)]'
                : heroSlotRole === 'micro-line'
                  ? 'text-white/90 drop-shadow-[0_2px_10px_rgba(0,0,0,0.34)]'
                  : 'text-white drop-shadow-md';
          const heroTypographyClass =
            heroSlotRole === 'heading'
              ? 'font-light tracking-tight leading-[0.95]'
              : heroSlotRole === 'subheading'
                ? 'font-normal leading-relaxed'
                : heroSlotRole === 'micro-line'
                  ? 'font-semibold tracking-tight'
                  : '';
          const textStyle = isHeroSlotBlock
            ? resolveHeroSlotTypographyStyle(
                (block.content ?? {}) as Record<string, unknown>,
                (heroSlotRole as HeroSlotRole) || 'subheading',
                typography,
                fontLibrary,
                { fluid: useFluidSectionTypography }
              )
            : resolveTypographyStyle(
                getTypographyPreset(typography, genericPresetKey),
                fontLibrary,
                { fluid: useFluidSectionTypography }
              );
          const textDesignStyle = normalizeButtonDesignStyle(block.style);
          const textRegularStyle = buttonVisualStateToReactStyle(textDesignStyle, fontLibrary, {
            previewViewport: isAdmin ? adminPreview?.viewport : undefined,
          });
          const blockWidthPx =
            layerSize.width > 0 ? (w / TOTAL_COLUMNS) * layerSize.width * blockTransform.widthScale : 0;
          const hasExplicitTextSize =
            blockStyleMeta?.fontSize !== undefined ||
            blockStyleMeta?.fontSizeDesktop !== undefined ||
            blockStyleMeta?.fontSizeMobile !== undefined;
          const headingFitStyle = hasExplicitTextSize
            ? {}
            : resolveCanvasHeadingFitStyle({
                text,
                textStyle: {
                  ...textStyle,
                  ...textRegularStyle,
                },
                blockWidthPx,
                isHeading,
              });
          const rawTextAlign = typeof blockStyleMeta?.textAlign === 'string' ? blockStyleMeta.textAlign : undefined;
          const resolvedTextAlign =
            rawTextAlign === 'center' || rawTextAlign === 'right' || rawTextAlign === 'left'
              ? rawTextAlign
              : undefined;
          const textContentStyle: React.CSSProperties = {
            ...textStyle,
            ...textRegularStyle,
            ...headingFitStyle,
            maxWidth: '100%',
            textAlign: resolvedTextAlign,
            ...(isHeading
              ? {
                  overflowWrap: 'normal',
                  wordBreak: 'normal',
                  textWrap: 'balance',
                  hyphens: 'none',
                }
              : {}),
          };
          const isInlineEditingTextBlock =
            isAdmin && !isInteractPreview && editingTextBlockId === block.id;
          const idleTextCursorClass =
            isAdmin && blockSelected && !isInlineEditingTextBlock ? 'cursor-text' : '';
          const shouldUseTextContentHit = isHeading;
          const sharedTextSelectionProps =
            !isInlineEditingTextBlock && selectionTarget === 'content' && !shouldUseTextContentHit
              ? selectableBlockHitProps
              : {};
          const textContentSelectionProps =
            !isInlineEditingTextBlock && selectionTarget === 'content' && shouldUseTextContentHit
              ? selectableBlockHitProps
              : {};
          const inlineEditActivationProps =
            isAdmin && !isInteractPreview && !isInlineEditingTextBlock
              ? {
                  'data-builder-inline-edit-target': 'true',
                  tabIndex: blockSelected ? 0 : undefined,
                  onDoubleClick: (event: React.MouseEvent<HTMLElement>) => {
                    if ((event.target as HTMLElement | null)?.closest('[data-builder-text-editor="true"]')) return;
                    event.preventDefault();
                    event.stopPropagation();
                    adminPreview?.onStartTextEdit?.(block.id);
                  },
                  onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => {
                    if (!blockSelected || event.key !== 'Enter') return;
                    event.preventDefault();
                    event.stopPropagation();
                    adminPreview?.onStartTextEdit?.(block.id);
                  },
                }
              : {};
          const renderTextSelectionOverlay = () =>
            isInlineEditingTextBlock ? null : renderSelectionOverlay(block);
          const renderInlineTextEditor = (editorContainerClassName: string) => (
            <div
              className={`relative w-full ${editorContainerClassName}`.trim()}
              data-builder-text-editor="true"
              data-builder-allow-interaction
            >
              <RichTextEditor
                id={`canvas-inline-text-${block.id}`}
                value={lexicalState ?? coerceLexicalStateString('', text)}
                onChange={(value) =>
                  adminPreview?.onUpdateTextContent?.(block.id, stripLexicalInlineFontFamily(value))
                }
                onBlur={() => adminPreview?.onEndTextEdit?.(block.id)}
                onRequestClose={() => adminPreview?.onEndTextEdit?.(block.id)}
                placeholder="Write your content..."
                autoFocus
                showToolbar
                showFontFamilyControl={false}
                fontOptions={richTextFontOptions}
                surfaceClassName="overflow-visible border-blue-200/90 shadow-[0_8px_22px_rgba(15,23,42,0.12)]"
                toolbarClassName="absolute inset-x-0 -top-12 z-30"
                editorClassName="min-h-[72px] bg-white/96"
              />
            </div>
          );
          const debugTextEditBadge = null;

          const textBlockWrapperStyle = !usesFixedFrameText && !isHeroSlotBlock && !DIAGNOSTIC_FORCE_TEXT_FRAME_TRUTH
            ? {
                ...blockStyle,
                width: projectedWidth,
                height: 'auto' as const,
              }
            : blockStyle;

          if (isSubscribeSlotBlock) {
            const subscribeToneClass =
              subscribeSlotRole === 'heading'
                ? 'text-stone-900'
                : subscribeSlotRole === 'consent'
                  ? 'text-stone-600'
                  : 'text-stone-700';
            const subscribeTypographyClass =
              subscribeSlotRole === 'heading'
                ? 'text-4xl md:text-5xl font-light'
                : subscribeSlotRole === 'description' || subscribeSlotRole === 'benefits'
                  ? 'text-lg leading-relaxed whitespace-pre-line'
                  : subscribeSlotRole === 'consent'
                    ? 'text-sm leading-6'
                    : 'text-base';
            const isInputLike =
              subscribeSlotRole === 'first-name' || subscribeSlotRole === 'email' || variant === 'input';

          return (
            <div
              key={block.id}
              {...blockWrapperProps}
              style={textBlockWrapperStyle}
            >
              <div
                ref={(node) => setTextMeasureNode(block.id, node)}
                {...sharedTextSelectionProps}
                {...inlineEditActivationProps}
                className={
                  isInputLike
                    ? `relative flex w-full items-center rounded-md border border-stone-300 bg-white px-6 py-4 text-stone-400 ${idleTextCursorClass}`.trim()
                    : `relative w-full ${idleTextCursorClass}`.trim()
                }
              >
                  {debugTextEditBadge}
                  {isInlineEditingTextBlock && !isInputLike ? (
                    renderInlineTextEditor('')
                  ) : (
                    <p
                      data-builder-editor-bounds="content"
                      {...textContentSelectionProps}
                      className={`${subscribeToneClass} ${subscribeTypographyClass} whitespace-pre-wrap break-words [overflow-wrap:anywhere]`}
                      style={textStyle}
                    >
                      {text}
                    </p>
                  )}
                  {renderTextSelectionOverlay()}
                </div>
              </div>
            );
          }

          if (isEnquirySlotBlock) {
            const enquiryToneClass =
              enquirySlotRole === 'heading'
                ? 'text-stone-900'
                : enquirySlotRole === 'empty-cart' || enquirySlotRole === 'footer'
                  ? 'text-stone-600'
                  : 'text-stone-700';
            const enquiryTypographyClass =
              enquirySlotRole === 'heading'
                ? 'text-4xl md:text-5xl font-light'
                : enquirySlotRole === 'description'
                  ? 'text-lg leading-relaxed whitespace-pre-line'
                  : enquirySlotRole === 'newsletter'
                    ? 'text-sm leading-6'
                    : enquirySlotRole === 'empty-cart' || enquirySlotRole === 'footer'
                      ? 'text-sm leading-6'
                      : 'text-base';
            const isInputLike =
              enquirySlotRole === 'name-field' || enquirySlotRole === 'email-field';
            const isTextareaLike = enquirySlotRole === 'comments-field';
            const fieldLabel = String((block.content?.label as string) ?? '').trim();

            if (isInputLike || isTextareaLike) {
              return (
                <div key={block.id} {...blockWrapperProps} style={textBlockWrapperStyle}>
                  <div
                    {...sharedTextSelectionProps}
                    {...inlineEditActivationProps}
                    className={`relative flex w-full flex-col gap-1 rounded-md border border-stone-300 bg-white px-4 py-3 ${idleTextCursorClass}`.trim()}
                    onDoubleClick={undefined}
                  >
                    {fieldLabel ? (
                      <p className="text-xs font-medium uppercase tracking-[0.08em] text-stone-600">
                        {fieldLabel}
                      </p>
                    ) : null}
                    <div
                      className={`w-full rounded-md border border-stone-200 bg-stone-50 px-3 py-2 ${
                        isTextareaLike ? 'min-h-[56px]' : ''
                      }`}
                    >
                  <p className="text-sm text-stone-500">{text}</p>
                    </div>
                    {renderTextSelectionOverlay()}
                  </div>
                </div>
              );
            }

            return (
              <div key={block.id} {...blockWrapperProps} style={textBlockWrapperStyle}>
                <div
                  ref={(node) => setTextMeasureNode(block.id, node)}
                  {...sharedTextSelectionProps}
                  {...inlineEditActivationProps}
                  className={`relative h-full w-full rounded-lg border border-stone-200 bg-white/92 px-4 py-3 ${idleTextCursorClass} ${
                    enquirySlotRole === 'empty-cart' ? 'bg-amber-50/70' : ''
                  }`}
                >
                  {debugTextEditBadge}
                  {isInlineEditingTextBlock ? (
                    renderInlineTextEditor('')
                  ) : (
                      <p
                        data-builder-editor-bounds="content"
                        {...textContentSelectionProps}
                        className={`${enquiryToneClass} ${enquiryTypographyClass} whitespace-pre-wrap break-words [overflow-wrap:anywhere]`}
                        style={textStyle}
                      >
                      {text}
                    </p>
                  )}
                  {renderTextSelectionOverlay()}
                </div>
              </div>
            );
          }

          return (
            <div
              key={block.id}
              {...blockWrapperProps}
              style={textBlockWrapperStyle}
            >
              {(() => {
                const resolvedSurfaceVariant = resolveTextSurfaceVariant(block.style?.surfaceVariant);
                const surfacePadding = String((block.style?.padding as string) ?? '').toLowerCase();
                const surfaceClass = getTextSurfaceClasses(resolvedSurfaceVariant);
                const surfacePaddingClass = surfacePadding === 'lg'
                  ? 'px-6 py-5'
                  : surfacePadding === 'md'
                    ? 'px-5 py-4'
                    : surfacePadding === 'sm'
                      ? 'px-4 py-3'
                      : '';
                const genericTextToneClass = isEyebrow
                  ? 'text-stone-600'
                  : getTextSurfaceToneClass(resolvedSurfaceVariant);
                const genericTextVariantClass = isEyebrow
                  ? 'text-xs font-semibold uppercase tracking-[0.14em]'
                  : isQuote
                    ? 'text-xl italic leading-relaxed'
                    : variant === 'hero-heading'
                      ? 'font-light tracking-tight leading-[0.95]'
                      : '';

                return (
              <div
                ref={(node) => setTextMeasureNode(block.id, node)}
                {...sharedTextSelectionProps}
                {...inlineEditActivationProps}
                className={
                  isHeroSlotBlock
                    ? `relative h-full w-full p-0 ${idleTextCursorClass}`.trim()
                    : usesFixedFrameText
                      ? `relative flex h-full w-full min-w-0 overflow-hidden ${surfaceClass} ${surfacePaddingClass} ${idleTextCursorClass}`.trim()
                    : `relative w-full min-w-0 ${surfaceClass} ${surfacePaddingClass} ${idleTextCursorClass}`.trim()
                }
              >
                {debugTextEditBadge}
                {isInlineEditingTextBlock ? (
                  renderInlineTextEditor('')
                ) : (
                  <>
                    {lexicalMode && lexicalHtml ? (
                      <div
                        data-builder-text-content="true"
                        data-builder-editor-bounds="content"
                        {...textContentSelectionProps}
                        className={
                          isHeroSlotBlock
                            ? `${heroToneClass} ${heroTypographyClass}`
                            : usesFixedFrameText
                              ? `${genericTextToneClass} ${genericTextVariantClass} min-h-0 w-full whitespace-pre-wrap break-words [overflow-wrap:anywhere] [&>p]:mb-3 [&>p:last-child]:mb-0 [&>ul]:list-disc [&>ul]:pl-5 [&>ol]:list-decimal [&>ol]:pl-5`
                            : `${genericTextToneClass} ${genericTextVariantClass} whitespace-pre-wrap break-words [overflow-wrap:anywhere] [&>p]:mb-3 [&>p:last-child]:mb-0 [&>ul]:list-disc [&>ul]:pl-5 [&>ol]:list-decimal [&>ol]:pl-5`
                        }
                        style={textContentStyle}
                        dangerouslySetInnerHTML={{ __html: lexicalHtml }}
                      />
                    ) : (
                      <p
                        data-builder-text-content="true"
                        data-builder-editor-bounds="content"
                        {...textContentSelectionProps}
                        className={
                          isHeroSlotBlock
                            ? `${heroToneClass} ${heroTypographyClass}`
                            : usesFixedFrameText
                              ? `${genericTextToneClass} ${genericTextVariantClass} min-h-0 w-full whitespace-pre-wrap break-words [overflow-wrap:anywhere]`
                            : `${genericTextToneClass} ${genericTextVariantClass} whitespace-pre-wrap break-words [overflow-wrap:anywhere]`
                        }
                        style={textContentStyle}
                      >
                        {text}
                      </p>
                    )}
                  </>
                )}
                {renderTextSelectionOverlay()}
              </div>
                );
              })()}
            </div>
          );
}
